"use client";

import { useEffect } from 'react';
import {
  getStoredBrandTheme,
  setStoredBrandTheme,
} from '@/utils/storage/safeLocalStorage';

/**
 * Hook to apply the saved brand theme on app initialization
 * This should be used in the root layout to ensure the theme is applied immediately
 * Requirements summary: blocked browser storage falls back to the default
 * CheerVue theme instead of throwing on auth/mobile Safari pages.
 */
export function useBrandTheme() {
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;
    
    const savedTheme = getStoredBrandTheme();
    
    // v1 = blue (add brand-v1). v2 = maroon default (remove brand-v1).
    if (savedTheme === 'v1') {
      document.documentElement.classList.add('brand-v1');
    } else {
      document.documentElement.classList.remove('brand-v1');
    }
  }, []);
}

/**
 * Get the current brand theme
 */
export function getBrandTheme(): 'v1' | 'v2' {
  if (typeof window === 'undefined') return 'v2'; // Default to v2
  const savedTheme = getStoredBrandTheme();
  return savedTheme === 'v1' ? 'v1' : 'v2'; // Default to v2 if not explicitly v1
}

/**
 * Set the brand theme
 */
export function setBrandTheme(theme: 'v1' | 'v2') {
  if (typeof window === 'undefined') return;
  
  setStoredBrandTheme(theme);
  
  if (theme === 'v1') {
    document.documentElement.classList.add('brand-v1');
  } else {
    document.documentElement.classList.remove('brand-v1');
  }
}

